import { ExamEditor as CampfireExamEditor } from "@campfirelearning/sdk-lti";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";

import { gql } from "@examspark/api";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { useLtiParams } from "../activities/ReplaceActivityItem";

const StyledExamEditor = styled.div`
  height: 100%;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    box-sizing: border-box;
  }
`;

const ExamEditor = ({ examId }: { examId?: string }) => {
  const consumer = useRef<CampfireExamEditor | null>(null);
  const ltiParams = useLtiParams();
  const authorizationContext = useAuthorizationContext();

  useEffect(() => {
    if (ltiParams == null) {
      console.info("Waiting for LTI params");
      return;
    }

    consumer.current = new CampfireExamEditor({
      id: examId,
      params: ltiParams,
      domain: process.env.REACT_APP_REST_API_URL ?? "https://api.examspark.com",
      signingFunction:
        (launchUrl: string) => async (params: Record<string, string>) => {
          try {
            const signer = await gql.operations.app.signCampfireLti(
              launchUrl,
              params,
              authorizationContext,
            );

            return {
              ...params,
              oauth_signature: signer.data?.signCampfireLti.signature ?? "",
            };
          } catch (e) {
            console.error(e);
          }

          return params;
        },
    });

    const debug = (message: MessageEvent<unknown>) => {
      console.debug(message.type, ": ", message.data);
    };

    consumer.current.addEventListener("message", debug);
    consumer.current.addEventListener("message:unhandled", debug);

    consumer.current.mount("tool").then(() => {
      console.log("Mounted!");
    });

    return () => {
      consumer.current?.removeEventListener("message", debug);
      consumer.current?.removeEventListener("message:unhandled", debug);
    };
  }, [authorizationContext, examId, ltiParams]);

  return (
    <StyledExamEditor>
      <iframe title="Exam Editor" id="tool" name="tool"></iframe>
    </StyledExamEditor>
  );
};

export default ExamEditor;
